import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import useProperty from 'hooks/Offers/useProperty'
import { useActiveAmenityFilters } from 'hooks/useActiveAmenityFilters'
import { EmptyArray } from 'lib/array/arrayUtils'
import { reorderAmenitiesByActiveFilters, reorderDescribedAmenitiesByActiveFilters } from 'lib/offer/amenitiesUtils'
import getPrioritizedBedbankAmenities from 'lib/offer/getPrioritizedBedbankAmenities'
import { useMemo } from 'react'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import SearchTileAmenitiesLoadingSkeleton from '../shared/SearchTileAmenitiesLoadingSkeleton'
import type { SearchTileRowInclusion } from '../shared/SearchTileRowInclusionItem'
import SearchTileStackAmenitiesList from '../shared/SearchTileStackAmenitiesList'
import { mapPropertyPopularFacilitiesToAmenities } from './propertyPopularFacilities'

const META_SEARCH_MOBILE_V2_MAX_AMENITIES = 4

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  shownRateInclusions: Array<App.OfferInclusion> | undefined
  propertyId?: string
  truncateAfter?: number
  singleColumn?: boolean
  maxAmenitiesToShow?: number
}

function BedbankMetaSearchTileAmenities({
  offer,
  shownRateInclusions,
  propertyId,
  truncateAfter,
  singleColumn,
  maxAmenitiesToShow,
}: Props) {
  const isMetaSearchMobileV2 = useMetaSearchMobileV2Enabled()
  const activeAmenityFilters = useActiveAmenityFilters()
  const [propertyContent, isFetchingPropertyContent] = useProperty(propertyId)
  const isFetchingPropertyAmenities = !!propertyId && isFetchingPropertyContent && !propertyContent
  const propertyContentAmenities = useMemo<Array<SearchTileRowInclusion>>(() => {
    if (!propertyContent?.popularFacilities.length) return EmptyArray
    return mapPropertyPopularFacilitiesToAmenities(propertyContent.popularFacilities)
  }, [propertyContent?.popularFacilities])
  const amenities = useMemo<Array<SearchTileRowInclusion>>(() => {
    const priorityVariant = isMetaSearchMobileV2 ? 'meta-search-v2' : 'default'

    if (propertyContentAmenities.length > 0) {
      return getPrioritizedBedbankAmenities({
        shownRateInclusions,
        popularFacilities: [],
        facilityAmenities: activeAmenityFilters.size > 0
          ? reorderDescribedAmenitiesByActiveFilters(propertyContentAmenities, activeAmenityFilters)
          : propertyContentAmenities,
        priorityVariant,
      })
    }

    return getPrioritizedBedbankAmenities({
      shownRateInclusions,
      popularFacilities: activeAmenityFilters.size > 0
        ? reorderAmenitiesByActiveFilters(offer.popularFacilities, activeAmenityFilters)
        : offer.popularFacilities,
      priorityVariant,
    })
  }, [activeAmenityFilters, isMetaSearchMobileV2, offer.popularFacilities, propertyContentAmenities, shownRateInclusions])
  const amenityLimit = isMetaSearchMobileV2
    ? META_SEARCH_MOBILE_V2_MAX_AMENITIES
    : (maxAmenitiesToShow ?? truncateAfter)

  if (isFetchingPropertyAmenities) return <SearchTileAmenitiesLoadingSkeleton />

  return <>
    {!!amenities.length && <SearchTileStackAmenitiesList amenities={amenities} truncateAfter={amenityLimit} singleColumn={singleColumn} />}
    <LoyaltyInclusions offer={offer} variant="search-tile" />
  </>
}

export default BedbankMetaSearchTileAmenities
